/**
 * Camera plugin: owns CameraRig and ViewportCamera services and keeps each
 * view's managed camera in sync with its rig state.
 */

import * as THREE from 'three'
import { DUAL_BACKEND_GRAPHICS_REQUIREMENTS } from '@/graphics/GraphicsRequirements'
import type { EngineContext } from '@/runtime/EngineContext'
import type { EngineEventMap } from '@/runtime/EventBus'
import type {
  CameraAnimationTargetRef,
  CameraBindingHost,
  CameraPatchOptions,
  CameraResolvedTarget,
  CameraStatePatch,
} from '@/shared/types/animation'
import { CameraMode } from '@/shared/types/core'
import type { Plugin } from '@/shared/types/plugin'
import type {
  CameraRig,
  RenderPipeline,
  ViewportCamera as ViewportCameraService,
  ViewRegistry,
} from '@/shared/types/services'
import type { ViewFrameContext } from '@/shared/types/view'
import { ActiveViewCameraRigFacade } from './ActiveViewCameraRigFacade'
import { ActiveViewViewportCameraFacade } from './ActiveViewViewportCameraFacade'
import { type CameraRigEventSink, CameraRigImpl } from './CameraRig'
import { ViewportCamera } from './ViewportCamera'

interface CameraViewResource {
  rig: CameraRigImpl
  viewport: ViewportCamera
}

/**
 * 相机插件，为每个视图维护独立的 CameraRig 与 ViewportCamera。
 */
export class CameraPlugin implements Plugin {
  /** 插件名称 */
  readonly name = 'camera'
  /** 相机服务只维护 CPU 侧 CameraRig/ViewportCamera，可在 WebGL/WebGPU 下复用。 */
  readonly graphicsRequirements = DUAL_BACKEND_GRAPHICS_REQUIREMENTS
  /** 插件启动优先级 */
  readonly priority = 20
  /** 依赖的服务名列表 */
  readonly dependencies = ['ViewRegistry'] as const
  /** 对外提供的服务名列表 */
  readonly provides = ['CameraRig', 'ViewportCamera', 'CameraBindingHost'] as const

  private rig!: CameraRig
  private viewport!: ViewportCameraService
  private context!: EngineContext
  private viewRegistry: ViewRegistry | null = null
  private renderPipeline: RenderPipeline | null = null
  private readonly cameraBindingHost: CameraBindingHost = {
    resolveCameraTarget: (ref) => this.resolveCameraTarget(ref),
    applyCameraStatePatch: (viewId, patch, options) =>
      this.applyCameraStatePatch(viewId, patch, options),
    cancelCameraTransition: (viewId) => this.cancelCameraTransition(viewId),
  }
  private readonly resources = new Map<string, CameraViewResource>()
  private _disposed = false
  private readonly _preparedFrames = new WeakSet<ViewFrameContext>()
  private readonly handleViewCreated = (event: { viewId: string }) => {
    this.ensureViewResource(event.viewId)
  }
  private readonly handleViewRemoved = (event: { viewId: string }) => {
    this.disposeViewResource(event.viewId)
  }
  private readonly handleViewActivated = (event: {
    viewId: string
    previousViewId: string | null
  }) => {
    const current = this.resources.get(event.viewId)
    if (!current) {
      return
    }
    const previousMode =
      event.previousViewId && this.resources.get(event.previousViewId)
        ? this.resources.get(event.previousViewId)!.rig.mode
        : current.rig.mode

    this.context.events.emit('camera.changed', { state: current.rig.getState() })
    if (previousMode !== current.rig.mode) {
      this.context.events.emit('camera.modeChanged', {
        mode: current.rig.mode,
        previous: previousMode,
      })
    }
  }

  /**
   * 初始化插件，注册 CameraRig 与 ViewportCamera 的代理服务。
   *
   * @param context - 引擎上下文
   */
  onInit(context: EngineContext): void {
    this.context = context
    this.rig = new ActiveViewCameraRigFacade(() => this.getActiveResource().rig)
    this.viewport = new ActiveViewViewportCameraFacade(() => this.getActiveResource().viewport)
    context.registerService('CameraRig', this.rig)
    context.registerService('ViewportCamera', this.viewport)
    context.registerService('CameraBindingHost', this.cameraBindingHost)
  }

  /** 启动插件，为已有视图构建资源并订阅视图事件 */
  onStart(): void {
    this.viewRegistry = this.context.getService('ViewRegistry')
    this.renderPipeline = this.context.hasService('RenderPipeline')
      ? this.context.getService('RenderPipeline')
      : null

    for (const view of this.viewRegistry.list()) {
      this.ensureViewResource(view.id)
    }

    this.context.events.on('view.created', this.handleViewCreated)
    this.context.events.on('view.removed', this.handleViewRemoved)
    this.context.events.on('view.activated', this.handleViewActivated)
  }

  /**
   * 渲染指定视图前同步对应相机资源。
   *
   * @param frame - 视图帧上下文
   */
  onRenderView(frame: ViewFrameContext): void {
    if (this._preparedFrames.has(frame)) return
    this.syncViewResource(frame.viewId, frame.viewRect)
    frame.camera = this.ensureViewResource(frame.viewId).viewport.getActiveCamera()
  }

  onPrepareViews(frames: readonly ViewFrameContext[]): void {
    let sharedBounds: THREE.Box3 | undefined
    // 透视视图使用同一场景边界，每次准备只遍历一次；不跨帧缓存动态资产。
    const getSceneBounds = () => (sharedBounds ??= this.context.sceneBounds.getWorldBoundingBox())
    for (const frame of frames) {
      if (this._preparedFrames.has(frame)) continue
      this.syncViewResource(frame.viewId, frame.viewRect, getSceneBounds)
      frame.camera = this.ensureViewResource(frame.viewId).viewport.getActiveCamera()
      this._preparedFrames.add(frame)
    }
  }

  /** 销毁插件，释放所有视图的相机资源 */
  onDestroy(): void {
    if (this._disposed) return
    this._disposed = true
    this.context.events.off('view.created', this.handleViewCreated)
    this.context.events.off('view.removed', this.handleViewRemoved)
    this.context.events.off('view.activated', this.handleViewActivated)

    for (const viewId of Array.from(this.resources.keys())) {
      this.disposeViewResource(viewId)
    }

    this.rig.dispose()
    this.viewport.dispose()
    this.viewRegistry = null
  }

  private ensureViewResource(viewId: string): CameraViewResource {
    const existing = this.resources.get(viewId)
    if (existing) {
      return existing
    }

    const rig = new CameraRigImpl(this.createEventSink(viewId))
    const viewport = new ViewportCamera()
    const resource = { rig, viewport }

    this.resources.set(viewId, resource)
    this.viewRegistry?.setExtension(viewId, 'cameraRig', rig)
    this.viewRegistry?.setExtension(viewId, 'viewportCamera', viewport)

    return resource
  }

  private disposeViewResource(viewId: string): void {
    const resource = this.resources.get(viewId)
    if (!resource) {
      return
    }

    resource.rig.dispose()
    resource.viewport.dispose()
    this.resources.delete(viewId)
  }

  private getActiveResource(): CameraViewResource {
    const activeViewId = this.viewRegistry?.getActive()?.id
    if (activeViewId) {
      return this.ensureViewResource(activeViewId)
    }
    const first = this.resources.values().next().value as CameraViewResource | undefined
    if (first) {
      return first
    }
    return this.ensureViewResource(this.viewRegistry?.primaryViewId ?? 'primary')
  }

  private resolveCameraTarget(ref: CameraAnimationTargetRef): CameraResolvedTarget | null {
    const viewId =
      ref.viewId ??
      this.viewRegistry?.getActive()?.id ??
      this.viewRegistry?.primaryViewId ??
      this.resources.keys().next().value ??
      'primary'

    if (this.viewRegistry && !this.viewRegistry.get(viewId)) {
      return null
    }

    const resource = this.ensureViewResource(viewId)
    return {
      key: `camera:${viewId}`,
      viewId,
      rig: resource.rig,
    }
  }

  private applyCameraStatePatch(
    viewId: string,
    patch: CameraStatePatch,
    options?: CameraPatchOptions
  ): boolean {
    if (this.viewRegistry && !this.viewRegistry.get(viewId)) {
      return false
    }

    this.ensureViewResource(viewId).rig.applyStatePatch(patch, options)
    return true
  }

  private cancelCameraTransition(viewId: string): boolean {
    if (this.viewRegistry && !this.viewRegistry.get(viewId)) {
      return false
    }

    this.ensureViewResource(viewId).rig.cancelTransition()
    return true
  }

  private syncViewResource(
    viewId: string,
    viewportRect: { x: number; y: number; width: number; height: number },
    getSceneBounds?: () => THREE.Box3
  ): void {
    const resource = this.ensureViewResource(viewId)
    const state = resource.rig.getState()
    const orthographic = state.mode === CameraMode.Orthographic
    let frustum: THREE.Frustum | undefined
    if (orthographic) {
      resource.viewport.syncFromRig(state, resource.rig.near, resource.rig.far, viewportRect)
      if (viewportRect.width > 0 && viewportRect.height > 0) {
        const camera = resource.viewport.getOrthographicCamera()
        const projectionView = new THREE.Matrix4()
          .makeScale(0.5, 0.5, 1)
          .multiply(camera.projectionMatrix)
          .multiply(camera.matrixWorldInverse)
        frustum = new THREE.Frustum().setFromProjectionMatrix(projectionView)
        // 仅使用带半个视口余量的侧面；旧深度范围不得阻止新内容进入。
        frustum.planes[4].setComponents(0, 0, 0, 1)
        frustum.planes[5].setComponents(0, 0, 0, 1)
      }
    }
    const cameraBounds =
      !orthographic && getSceneBounds
        ? getSceneBounds().clone()
        : this.context.sceneBounds.getWorldBoundingBox(frustum ? { frustum } : {})
    if (this.renderPipeline) {
      const contributorBounds = this.renderPipeline.collectCameraClipBounds({ viewId })
      if (!contributorBounds.isEmpty() && (!frustum || frustum.intersectsBox(contributorBounds))) {
        cameraBounds.union(contributorBounds)
      }
    }
    resource.rig.updateNearFar(cameraBounds)
    if (orthographic) {
      for (const camera of [
        resource.viewport.getPerspectiveCamera(),
        resource.viewport.getOrthographicCamera(),
      ]) {
        camera.near = resource.rig.near
        camera.far = resource.rig.far
        camera.updateProjectionMatrix()
      }
    } else {
      resource.viewport.syncFromRig(
        resource.rig.getState(),
        resource.rig.near,
        resource.rig.far,
        viewportRect
      )
    }
    const layerMask = this.viewRegistry?.get(viewId)?.layerMask
    if (layerMask !== undefined) {
      resource.viewport.getPerspectiveCamera().layers.mask = layerMask
      resource.viewport.getOrthographicCamera().layers.mask = layerMask
    }
  }

  private createEventSink(viewId: string): CameraRigEventSink {
    return {
      emit: (
        event: 'camera.changed' | 'camera.modeChanged',
        data: EngineEventMap['camera.changed'] | EngineEventMap['camera.modeChanged']
      ) => {
        if (event === 'camera.changed') {
          const changed = data as EngineEventMap['camera.changed']
          this.context.events.emit('view.camera.changed', {
            viewId,
            state: changed.state,
          })
        } else {
          const modeChanged = data as EngineEventMap['camera.modeChanged']
          this.context.events.emit('view.camera.modeChanged', {
            viewId,
            mode: modeChanged.mode,
            previous: modeChanged.previous,
          })
        }

        if (this.viewRegistry?.getActive()?.id === viewId) {
          this.context.events.emit(event, data)
        }
      },
    }
  }
}
